import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import {
  commissionRunMonthKey,
  currentOpenCommissionRunMonthKey,
  formatCommissionRunMonthLong,
} from "./commissionRunMonth";
import { getCommissionRules, listCommissionRuns, money, type CommissionRunRow } from "./commissionsApi";

export function CommissionRunsPage() {
  const navigate = useNavigate();
  const currentRunMonth = currentOpenCommissionRunMonthKey();

  const listQuery = useQuery({
    queryKey: ["commissions", "runs"],
    queryFn: listCommissionRuns,
  });
  const rulesQuery = useQuery({
    queryKey: ["commissions", "rules"],
    queryFn: getCommissionRules,
  });
  const timingRule = rulesQuery.data?.rules.find((rule) => rule.id === "commission_timing");
  const cutoverDryRun = rulesQuery.data?.cutoverDryRun ?? rulesQuery.data?.statementsDryRun ?? false;
  const pageHoverHint = [
    timingRule != null
      ? `Monthly runs are created automatically for the latest complete payment month. Click a run row to review live lines and lock when ready. ${timingRule.summary}`
      : "Monthly runs are created automatically for the latest complete payment month. Each run is named for the calendar month of customer payments (see /commissions/rules).",
    cutoverDryRun
      ? "Cutover dry-run is on — statement generate and vendor invoice actions do not email reps or post to QuickBooks yet."
      : null,
  ]
    .filter(Boolean)
    .join(" ");

  const rows = listQuery.data?.rows ?? [];
  const columns: HubDataTableColumn<CommissionRunRow>[] = [
    {
      id: "payment_month",
      header: "Payment month",
      overflow: "nowrap",
      sortValue: (row) => commissionRunMonthKey(row.run_month),
      cell: (row) => {
        const paymentMonth = commissionRunMonthKey(row.run_month);
        const isCurrentRun = paymentMonth === currentRunMonth;
        return (
          <span>
            <span className={cn(isCurrentRun && "font-medium text-brand-900")}>
              {formatCommissionRunMonthLong(row.run_month)}
            </span>
            <span className="ml-2 text-slate-500 tabular-nums">{paymentMonth}</span>
            {isCurrentRun ? (
              <span className="ml-2 rounded bg-brand-100 px-1.5 py-0.5 text-xs font-medium text-brand-800">
                Current
              </span>
            ) : null}
          </span>
        );
      },
    },
    { id: "status", header: "Status", overflow: "nowrap", cell: (row) => row.status },
    {
      id: "total_base",
      header: "Base",
      align: "right",
      overflow: "nowrap",
      sortValue: (row) => Number(row.total_base),
      cell: (row) => money(row.total_base),
    },
    {
      id: "total_bonus",
      header: "Bonus",
      align: "right",
      overflow: "nowrap",
      sortValue: (row) => Number(row.total_bonus),
      cell: (row) => money(row.total_bonus),
    },
    {
      id: "total_stocking",
      header: "Stocking",
      align: "right",
      overflow: "nowrap",
      sortValue: (row) => Number(row.total_stocking),
      cell: (row) => money(row.total_stocking),
    },
    {
      id: "total_repless",
      header: "Repless",
      align: "right",
      overflow: "nowrap",
      sortValue: (row) => Number(row.total_repless),
      cell: (row) => money(row.total_repless),
    },
  ];

  return (
    <div className="space-y-6">
      <PageTitleWithHelp hoverHint={pageHoverHint}>
        Monthly commission runs
      </PageTitleWithHelp>

      <HubDataTable
        title="Runs"
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={listQuery.isLoading}
        loadingMessage="Loading…"
        error={listQuery.isError ? "Could not load runs." : null}
        empty="No hub runs yet."
        onRowClick={(row) => navigate(`/commissions/runs/${row.id}`)}
        rowClassName={(row) =>
          commissionRunMonthKey(row.run_month) === currentRunMonth
            ? "border-brand-200 bg-brand-50 hover:bg-brand-50/80"
            : undefined
        }
      />
    </div>
  );
}
